<!--
  Primary navigation — the package's Sidebar: brand, nav grouped by section,
  the collector's status in the footer, sign out.

   • Operations:  Dashboard (overview) and Traffic (live console).
   • Investigate: Events, HTTP requests, Domains, Detections.
   • Enforcement: Ban Review, Bans.
   • Inventory:   Services, Sources, Trusted IPs.
   • System:      Settings.

  The current item is the only place besides a primary button where the ember
  appears (NavItem draws it). Counts come from the shared shell poller — never
  from a second request.

  Under 880 px AppShell collapses this to a 64 px icon rail: labels, group
  captions and the footer text stay only for screen readers, icons stay centred.
-->
<script lang="ts">
	import type { Component } from 'svelte';
	import { Logo, NavItem, Sidebar, SidebarFooter, SidebarGroup, StatusDot, type Tone } from '@navitech/ui';
	import Activity from '@lucide/svelte/icons/activity';
	import ArrowLeftRight from '@lucide/svelte/icons/arrow-left-right';
	import Ban from '@lucide/svelte/icons/ban';
	import Globe from '@lucide/svelte/icons/globe';
	import LayoutDashboard from '@lucide/svelte/icons/layout-dashboard';
	import LayoutGrid from '@lucide/svelte/icons/layout-grid';
	import List from '@lucide/svelte/icons/list';
	import LogOut from '@lucide/svelte/icons/log-out';
	import RadioTower from '@lucide/svelte/icons/radio-tower';
	import ScrollText from '@lucide/svelte/icons/scroll-text';
	import Settings from '@lucide/svelte/icons/settings';
	import Shield from '@lucide/svelte/icons/shield';
	import ShieldAlert from '@lucide/svelte/icons/shield-alert';
	import ShieldCheck from '@lucide/svelte/icons/shield-check';
	import SquareCheckBig from '@lucide/svelte/icons/square-check-big';
	import { route, navigate, followLink } from '../lib/router';
	import { auth } from '../lib/api';
	import { resetIdentity } from '../lib/identity.svelte';
	import { resetFeatures } from '../lib/features.svelte';
	import { shellStats, resetShellStats } from '../lib/shellStats.svelte';

	type Item = { href: string; label: string; routeName: string; icon: Component<any>; count?: number | null };
	type Section = { title: string; items: Item[] };

	// Only two nav items carry a counter, and both are "work waiting for you":
	// open detections and ban suggestions pending review.
	const openDetections = $derived(shellStats.stats?.detections_open ?? null);
	const pendingBans = $derived(shellStats.pendingBans);

	const sections: Section[] = $derived([
		{
			title: 'Operations',
			items: [
				{ href: '/', label: 'Dashboard', routeName: 'dashboard', icon: LayoutDashboard },
				{ href: '/traffic', label: 'Traffic', routeName: 'traffic', icon: Activity },
			],
		},
		{
			title: 'Investigate',
			items: [
				{ href: '/events', label: 'Events', routeName: 'events', icon: List },
				{ href: '/http-requests', label: 'HTTP Requests', routeName: 'httpRequests', icon: ArrowLeftRight },
				{ href: '/domains', label: 'Domains', routeName: 'domains', icon: Globe },
				{ href: '/detections', label: 'Detections', routeName: 'detections', icon: ShieldCheck, count: openDetections },
			],
		},
		{
			title: 'Enforcement',
			items: [
				{ href: '/ban-review', label: 'Ban Review', routeName: 'banReview', icon: SquareCheckBig, count: pendingBans },
				{ href: '/bans', label: 'Bans', routeName: 'bans', icon: Ban },
				{ href: '/edge', label: 'Edge Guard', routeName: 'edge', icon: ShieldAlert },
			],
		},
		{
			title: 'Inventory',
			items: [
				{ href: '/services', label: 'Services', routeName: 'services', icon: LayoutGrid },
				{ href: '/sources', label: 'Sources', routeName: 'sources', icon: RadioTower },
				{ href: '/trusted-ips', label: 'Trusted IPs', routeName: 'trustedIps', icon: Shield },
			],
		},
		{
			title: 'System',
			items: [
				{ href: '/audit', label: 'Audit log', routeName: 'audit', icon: ScrollText },
				{ href: '/settings', label: 'Settings', routeName: 'settings', icon: Settings },
			],
		},
	]);

	function active(name: string, current: string): boolean {
		if (name === 'detections') return current === 'detections' || current === 'detectionDetail';
		if (name === 'edge') return current === 'edge' || current === 'edgeApp';
		if (name === 'events') return current === 'events' || current === 'eventDetail';
		if (name === 'domains') return current === 'domains' || current === 'domainDetail';
		return name === current;
	}

	// Footer dot: ok while every source is reporting, warn when some are
	// behind, err when the collector is silent (or we have no numbers yet).
	const up = $derived(shellStats.stats?.sources_up ?? 0);
	const total = $derived(shellStats.stats?.sources_total ?? 0);
	const ledTone: Tone = $derived(total > 0 && up === total ? 'ok' : up > 0 ? 'warn' : 'err');
	// Status is never colour alone: the dot says in words what its colour means.
	const ledLabel = $derived(
		shellStats.stats === null
			? 'Connecting'
			: ledTone === 'ok'
				? 'Healthy'
				: ledTone === 'warn'
					? 'Degraded'
					: 'Down',
	);
	const ledText = $derived(
		shellStats.stats === null ? 'Connecting…' : `Collector live · ${up}/${total} sources`,
	);

	async function doLogout() {
		// Asked before the local session is dropped: afterwards /auth/me is a
		// 401 and there is nothing left to ask.
		const viaSso = await auth
			.me()
			.then((u) => u.via_sso === true)
			.catch(() => false);

		try {
			await auth.logout();
		} finally {
			// Signing out has to look like signing out: without this the sign-in
			// page silently probes and, finding the provider's session alive,
			// signs the operator straight back in.
			try {
				sessionStorage.setItem('sm:sso-probe', String(Date.now()));
			} catch {
				/* nothing to remember it in */
			}
			resetIdentity();
			resetFeatures();
			resetShellStats();
			if (viaSso) {
				// Hands the browser to the IdP, which ends the SSO session and
				// tells the other Navitech applications to end theirs.
				auth.ssoLogout();
				return;
			}
			navigate('/login');
		}
	}
</script>

<Sidebar>
	{#snippet brand()}
		<Logo name="Firewall Manager" sub="Navitech · Phase 1" />
	{/snippet}

	{#each sections as section (section.title)}
		<SidebarGroup label={section.title}>
			{#each section.items as item (item.href)}
				<NavItem
					href={item.href}
					label={item.label}
					icon={item.icon}
					count={item.count || undefined}
					current={active(item.routeName, $route.name)}
					onclick={followLink}
				/>
			{/each}
		</SidebarGroup>
	{/each}

	{#snippet footer()}
		<SidebarFooter title={ledText}>
			{#snippet indicator()}
				<StatusDot tone={ledTone} label={ledLabel} pulse />
			{/snippet}
			{ledText}
		</SidebarFooter>
		<!-- Sign out is an action, not a destination, so it is a button drawn in
		     the nav item's anatomy; like the items it keeps only its icon in the
		     rail (880 px is the shell's breakpoint). -->
		<button
			type="button"
			onclick={doLogout}
			title="Sign out"
			class="flex cursor-pointer items-center gap-[11px] rounded-md border border-transparent px-[10px] py-[9px] text-left text-[13px] text-text-dim transition-colors duration-150 ease-ds hover:bg-surface-1 hover:text-text [@media(max-width:880px)]:justify-center [@media(max-width:880px)]:px-0"
		>
			<LogOut class="size-[18px] shrink-0" aria-hidden="true" />
			<span class="[@media(max-width:880px)]:sr-only">Sign out</span>
		</button>
	{/snippet}
</Sidebar>
